import { useCallback, useEffect, useReducer, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { simplifyMidi } from '../midi/simplifyMidi'
import { MidiPlayer } from '../midi/midiPlayer'
import MidiList from '../components/MidiSynth/MidiList'
import { midiReducer, type MidiItem } from '../components/MidiSynth/midiState'
import Dropzone from '../components/Dropzone/Dropzone'
import { type UploadFile } from '../upload/uploadAudio'
import { MIDI_SYNTH_FIRMWARE } from '../upload/fetchFirmware'
import { useToyMaker } from '../upload/useToyMaker'
import './MakeMusicPlayer.css'
import './MakeMidiSynth.css'

function isMidiFile(file: File): boolean {
  const lower = file.name.toLowerCase()
  return lower.endsWith('.mid') || lower.endsWith('.midi')
}

export default function MakeMidiSynth() {
  const [items, dispatch] = useReducer(midiReducer, [] as MidiItem[])
  const [playingId, setPlayingId] = useState<string | null>(null)
  const playerRef = useRef<MidiPlayer | null>(null)

  const stopPlayback = useCallback(() => {
    playerRef.current?.dispose()
    playerRef.current = null
    setPlayingId(null)
  }, [])

  useEffect(() => {
    return () => {
      playerRef.current?.dispose()
      playerRef.current = null
    }
  }, [])

  const intakeFile = useCallback(async (file: File, insertAt?: number) => {
    if (!isMidiFile(file)) return
    const id = crypto.randomUUID()
    dispatch({ type: 'add', id, sourceName: file.name, insertAt })
    try {
      const buf = await file.arrayBuffer()
      const bytes = new Uint8Array(buf)
      const { bytes: outBytes, summary } = simplifyMidi(bytes)
      dispatch({ type: 'parsed', id, bytes: outBytes, summary })
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to parse MIDI'
      dispatch({ type: 'error', id, message })
    }
  }, [])

  const intakeFiles = (incoming: File[]) => {
    for (const f of incoming) {
      if (isMidiFile(f)) intakeFile(f)
    }
  }

  const handleInsertFiles = useCallback(
    (targetIndex: number, files: File[]) => {
      const midis = files.filter(isMidiFile)
      midis.forEach((f, i) => intakeFile(f, targetIndex + i))
    },
    [intakeFile],
  )

  const handleRemove = useCallback(
    (id: string) => {
      if (playingId === id) stopPlayback()
      dispatch({ type: 'remove', id })
    },
    [playingId, stopPlayback],
  )

  const handleReorder = useCallback((from: number, to: number) => {
    dispatch({ type: 'reorder', from, to })
  }, [])

  const handlePlayToggle = useCallback(
    (id: string) => {
      if (playingId === id) {
        stopPlayback()
        return
      }
      stopPlayback()
      const item = items.find((it) => it.id === id)
      if (!item || item.status !== 'ready') return
      const player = new MidiPlayer(item.bytes, {
        onEnded: () => {
          if (playerRef.current === player) {
            playerRef.current = null
            setPlayingId(null)
          }
        },
      })
      playerRef.current = player
      setPlayingId(id)
      player.play().catch((err) => {
        console.error('[MakeMidiSynth] playback failed', err)
        if (playerRef.current === player) stopPlayback()
      })
    },
    [items, playingId, stopPlayback],
  )

  const readyCount = items.filter((it) => it.status === 'ready').length
  const hasReady = readyCount > 0
  const isParsing = items.some((it) => it.status === 'parsing')

  const toyMaker = useToyMaker({
    makerApp: 'midi-synth',
    hasPayload: hasReady,
    extraDisabled: isParsing,
    firmwareTarget: MIDI_SYNTH_FIRMWARE,
    appNameFallback: 'Oroblanco MIDI Synth',
    buildPayload: async () => {
      const payloadFiles: UploadFile[] = items
        .filter((it): it is Extract<MidiItem, { status: 'ready' }> => it.status === 'ready')
        .map((it, i) => ({
          name: `${String(i + 1).padStart(4, '0')}.mid`,
          blob: new Blob([it.bytes as BlobPart], { type: 'audio/midi' }),
        }))
      return { payloadFiles }
    },
  })

  return (
    <section className="music-player">
      <Link to="/" className="detail-back">&larr; Back to Toys</Link>
      <h1 className="page-title">MIDI Synth</h1>
      <p className="page-lede">
        Drop <b>.mid</b> files here. Each is simplified in your browser (drums dropped,
        duplicate tracks removed, melody promoted) before upload. Reorder the list to set
        playback order — files will be renamed <code>0001.mid</code>, <code>0002.mid</code>, …
      </p>

      <div className="card music-player__panel">
        <Dropzone onFiles={intakeFiles} accept=".mid,.midi,audio/midi" compact>
          <p className="music-player__dropzone-text">
            Drop <b>.mid</b> files here, or click to choose. Multiple files welcome.
          </p>
        </Dropzone>
        <MidiList
          items={items}
          playingId={playingId}
          onRemove={handleRemove}
          onReorder={handleReorder}
          onPlayToggle={handlePlayToggle}
          onInsertFiles={handleInsertFiles}
        />
      </div>

      <div className="music-player__actions">
        <span className="music-player__count">
          {readyCount} ready{isParsing ? ' (parsing…)' : ''}
        </span>
        <button
          type="button"
          className="btn btn-primary"
          disabled={!toyMaker.canSubmit}
          onClick={toyMaker.onDoneClick}
        >
          {toyMaker.isWorking ? 'Working…' : 'Done, make my toy'}
        </button>
      </div>

      {toyMaker.modals}
    </section>
  )
}
